import React, { useMemo, useState, useRef, useLayoutEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  IconButton,
  Chip,
  Link,
  Tooltip,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material'
import TouchAppIcon from '@mui/icons-material/TouchApp'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material'
import { shipmentService, accountService, driverService, vehicleService } from '../api/adminServices'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import ConfirmDialog from '../components/ConfirmDialog'
import { useToast } from '../components/useToast'
import PageRefreshButton from '../components/PageRefreshButton'
import { exportJson } from '../utils/exportJson'
import PriceBreakdownModal from '../components/PriceBreakdownModal'
import {
  formatBreakdownSummary,
  isPriceBreakdown,
  isPriceBreakdownColumnKey,
  normalizePriceBreakdown,
  PriceBreakdownData,
  shouldIncludeTableColumn,
} from '../utils/priceBreakdown'
import {
  formatDurationMinutesAsHm,
  isDurationMinutesField,
} from '../utils/propertyFormat'
import {
  accountDisplayName,
  buildEntityLookups,
  formatColumnHeader,
} from '../utils/entityDisplay'
import AccountDetailDialog from '../components/AccountDetailDialog'
import { renderEntityCell } from '../components/EntityAccountCell'

type TimeWindow = 'LAST_30_DAYS' | 'TODAY' | 'NEXT_7_DAYS' | 'NEXT_30_DAYS'
type ShipmentRequestStatus =
  | 'DRAFT'
  | 'NEW'
  | 'PENDING_CONFIRMATION'
  | 'PENDING_MATCHING'
  | 'PLANNED'
  | 'PARTIALLY_PLANNED'
  | 'IN_PROGRESS'
  | 'COMPLETED'

/** Tabs of the "Shipment Requests" page. DRAFT is deliberately left out; it has its own "Draft Requests" page. */
const DEFAULT_STATUS_TABS: ShipmentRequestStatus[] = [
  'NEW',
  'PENDING_CONFIRMATION',
  'PENDING_MATCHING',
  'PLANNED',
  'PARTIALLY_PLANNED',
  'IN_PROGRESS',
  'COMPLETED',
]

const RESET_TO_NEW_STATUSES: ShipmentRequestStatus[] = [
  'NEW',
  'PENDING_CONFIRMATION',
  'PENDING_MATCHING',
  'PARTIALLY_PLANNED',
]

function getRequestStatus(request: { status?: string; shipmentRequestStatus?: string }): string {
  return String(request.status || request.shipmentRequestStatus || '').toUpperCase()
}

function canResetShipmentRequestToNew(status: string): boolean {
  return RESET_TO_NEW_STATUSES.includes(status as ShipmentRequestStatus)
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shipment-request-tabpanel-${index}`}
      aria-labelledby={`shipment-request-tab-${index}`}
      style={value === index ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>}
    </div>
  )
}

interface ShipmentRequestsPageProps {
  /** Status tabs to show, in order. */
  statuses?: ShipmentRequestStatus[]
}

export default function ShipmentRequestsPage({ statuses = DEFAULT_STATUS_TABS }: ShipmentRequestsPageProps) {
  const getLocalizedLabel = useLocalizedLabel()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('TODAY')
  const [activeTab, setActiveTab] = useState(0)
  const [shipperFilter, setShipperFilter] = useState('')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [expandedShipments, setExpandedShipments] = useState<Set<string>>(new Set())
  const rootHeadRef = useRef<HTMLTableSectionElement>(null)
  const [rootHeaderHeight, setRootHeaderHeight] = useState(48)
  const nestedHeaderHeight = 40
  const [priceModal, setPriceModal] = useState<{
    open: boolean
    title: string
    breakdown: PriceBreakdownData | null
  }>({ open: false, title: '', breakdown: null })
  const [resetConfirmTarget, setResetConfirmTarget] = useState<{
    id: string
    status: string
  } | null>(null)
  const [accountDetailId, setAccountDetailId] = useState<string | null>(null)

  const resetToNewMutation = useMutation({
    mutationFn: (requestId: string) => shipmentService.resetShipmentRequestToNew(requestId),
    onSuccess: () => {
      toast.success('Shipment request reset to NEW')
      setResetConfirmTarget(null)
      queryClient.invalidateQueries({ queryKey: ['shipmentRequests', dateRange.from, dateRange.to] })
    },
    onError: (err: Error) => {
      toast.errorFrom(err, 'Failed to reset shipment request')
    },
  })

  const statusTabs = statuses

  // Format date to ISO_LOCAL_DATE_TIME (YYYY-MM-DDTHH:mm:ss) without timezone or milliseconds
  const formatToIsoLocalDateTime = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }

  // Calculate date range based on time window
  const getDateRange = (window: TimeWindow): { from: string; to: string } => {
    const now = new Date()
    let from: Date
    let to: Date

    switch (window) {
      case 'LAST_30_DAYS':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
        break
      case 'TODAY':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
        break
      case 'NEXT_7_DAYS':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 23, 59, 59)
        break
      case 'NEXT_30_DAYS':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30, 23, 59, 59)
        break
      default:
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
    }

    return {
      from: formatToIsoLocalDateTime(from),
      to: formatToIsoLocalDateTime(to),
    }
  }

  const dateRange = getDateRange(timeWindow)

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-shipment-requests'],
    queryFn: () => accountService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const { data: driversData } = useQuery({
    queryKey: ['drivers-for-shipment-requests'],
    queryFn: () => driverService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles-for-shipment-requests'],
    queryFn: () => vehicleService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const entityLookups = useMemo(
    () => buildEntityLookups(accountsData, driversData, vehiclesData),
    [accountsData, driversData, vehiclesData]
  )

  const shipperAccounts = useMemo(() => {
    const accounts = entityLookups.accountById
    return [...accounts.values()].filter((a) => String(a.type || '').toUpperCase() === 'SHIPPER')
  }, [entityLookups.accountById])

  // Fetch all shipment requests for the time window (no status filter)
  const {
    data: allShipmentRequests,
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['shipmentRequests', dateRange.from, dateRange.to],
    queryFn: async () => {
      const result = await shipmentService.getShipmentRequestsByTimeWindow(dateRange.from, dateRange.to)
      return Array.isArray(result) ? result : result?.content || result?.data || []
    },
    retry: 1,
  })

  // Filter by current status on client side
  const currentStatus = statusTabs[activeTab]
  const isNewTab = currentStatus === 'NEW' || currentStatus === 'DRAFT'
  const isPendingConfirmationTab = currentStatus === 'PENDING_CONFIRMATION'
  const isPendingMatchingTab = currentStatus === 'PENDING_MATCHING'
  const shipmentRequests = allShipmentRequests
    ? allShipmentRequests.filter((request: any) => {
        const requestStatus = String(request.status || request.shipmentRequestStatus || '').toUpperCase()
        if (requestStatus !== currentStatus) return false
        if (shipperFilter) {
          const shipperId = request.shipperAccountId || request.accountId
          if (shipperId !== shipperFilter) return false
        }
        return true
      })
    : []

  // Calculate counts for each status tab
  const getStatusCount = (status: ShipmentRequestStatus): number => {
    if (!allShipmentRequests) return 0
    return allShipmentRequests.filter((request: any) => {
      const requestStatus = String(request.status || request.shipmentRequestStatus || '').toUpperCase()
      return requestStatus === status
    }).length
  }

  const handleTimeWindowChange = (_event: React.MouseEvent<HTMLElement>, newValue: TimeWindow | null) => {
    if (newValue !== null) {
      setTimeWindow(newValue)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  // Helper to get enum name from field key
  const getEnumNameFromKey = (fieldKey: string, context?: 'shipment' | 'request'): string | null => {
    const lowerKey = fieldKey.toLowerCase()
    const enumFieldMap: Record<string, string> = {
      urgency: 'shipmentUrgency',
      shipmenturgency: 'shipmentUrgency',
      shipment_urgency: 'shipmentUrgency',
      status: context === 'shipment' ? 'shipmentStatus' : 'shipmentRequestStatus',
      shipmentrequeststatus: 'shipmentRequestStatus',
      shipment_request_status: 'shipmentRequestStatus',
      shipmentstatus: 'shipmentStatus',
      shipment_status: 'shipmentStatus',
      cargotype: 'cargoType',
      cargo_type: 'cargoType',
      paymentterms: 'paymentTerms',
      payment_terms: 'paymentTerms',
      currency: 'currency',
    }
    return enumFieldMap[lowerKey] || null
  }

  // Calculate age in a human-readable format
  const calculateAge = (fromDate: string | null | undefined, toDate?: string | null | undefined): string => {
    if (!fromDate || fromDate === 'null' || fromDate === '') return '-'
    
    try {
      const from = new Date(fromDate)
      const to = toDate ? new Date(toDate) : new Date()
      
      if (isNaN(from.getTime())) {
        // Try parsing with different formats
        const cleanedDate = fromDate.replace(/\.\d+$/, '') // Remove microseconds if present
        const from2 = new Date(cleanedDate)
        if (isNaN(from2.getTime())) return '-'
        
        const diffMs = to.getTime() - from2.getTime()
        if (diffMs < 0) return '-' // Future date
        
        const diffSeconds = Math.floor(diffMs / 1000)
        const diffMinutes = Math.floor(diffSeconds / 60)
        const diffHours = Math.floor(diffMinutes / 60)
        const diffDays = Math.floor(diffHours / 24)
        
        if (diffDays > 0) {
          const hours = diffHours % 24
          return hours > 0 ? `${diffDays}d ${hours}h` : `${diffDays}d`
        } else if (diffHours > 0) {
          const minutes = diffMinutes % 60
          return minutes > 0 ? `${diffHours}h ${minutes}m` : `${diffHours}h`
        } else if (diffMinutes > 0) {
          return `${diffMinutes}m`
        } else {
          return `${diffSeconds}s`
        }
      }
      
      if (isNaN(to.getTime())) return '-'
      
      const diffMs = to.getTime() - from.getTime()
      if (diffMs < 0) return '-' // Future date
      
      const diffSeconds = Math.floor(diffMs / 1000)
      const diffMinutes = Math.floor(diffSeconds / 60)
      const diffHours = Math.floor(diffMinutes / 60)
      const diffDays = Math.floor(diffHours / 24)
      
      if (diffDays > 0) {
        const hours = diffHours % 24
        return hours > 0 ? `${diffDays}d ${hours}h` : `${diffDays}d`
      } else if (diffHours > 0) {
        const minutes = diffMinutes % 60
        return minutes > 0 ? `${diffHours}h ${minutes}m` : `${diffHours}h`
      } else if (diffMinutes > 0) {
        return `${diffMinutes}m`
      } else {
        return `${diffSeconds}s`
      }
    } catch {
      return '-'
    }
  }

  // Get table columns from first item if available, excluding cargos and shipments (will be shown separately)
  const getColumns = () => {
    if (!shipmentRequests || shipmentRequests.length === 0) return []
    const firstItem = shipmentRequests[0]
    const baseColumns = Object.keys(firstItem).filter((key) =>
      shouldIncludeTableColumn(key, firstItem[key], ['cargos', 'shipments'])
    )
    // Add Cargos/Shipments column at the beginning
    const columns = [isNewTab ? 'cargos' : 'shipments', ...baseColumns]
    
    // Add Age column for New tab (after cargos), Pending Confirmation tab (after shipments), or Pending Matching tab (after shipments)
    if (isNewTab || isPendingConfirmationTab || isPendingMatchingTab) {
      // Insert Age column after the first column (cargos or shipments)
      columns.splice(1, 0, 'age')
    }
    
    return columns
  }

  const columns = getColumns()

  useLayoutEffect(() => {
    const head = rootHeadRef.current
    if (!head) return

    const measure = () => {
      setRootHeaderHeight(head.getBoundingClientRect().height)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(head)
    return () => observer.disconnect()
  }, [activeTab, isLoading, columns.length, shipmentRequests.length])

  const formatColumnLabel = (column: string): string => {
    if (column === 'cargos') return 'Cargos'
    if (column === 'shipments') return 'Shipments'
    if (column === 'age') return 'Age'
    return formatColumnHeader(column)
  }

  const renderPriceBreakdownLink = (
    title: string,
    breakdown: PriceBreakdownData
  ): React.ReactNode => (
    <Link
      component="button"
      variant="body2"
      onClick={(e) => {
        e.stopPropagation()
        setPriceModal({ open: true, title, breakdown })
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontWeight: 600,
        textAlign: 'start',
        verticalAlign: 'inherit',
      }}
    >
      <TouchAppIcon sx={{ fontSize: 16, opacity: 0.8 }} />
      {formatBreakdownSummary(breakdown, (code) => getLocalizedLabel('currency', code))}
    </Link>
  )

  const renderPriceBreakdownCell = (
    key: string,
    value: unknown,
    fallbackCurrency?: string | null
  ): React.ReactNode | null => {
    if (!isPriceBreakdown(value) && !isPriceBreakdownColumnKey(key)) return null
    const breakdown = normalizePriceBreakdown(value, fallbackCurrency)
    if (!breakdown) return null
    return renderPriceBreakdownLink(formatColumnLabel(key), breakdown)
  }

  const formatValue = (
    key: string,
    value: any,
    request?: any,
    options?: { priceBreakdown?: boolean }
  ): any => {
    // Handle Age column FIRST (before null check, since age is computed and value is null)
    if (key === 'age') {
      if (!request) return '-'
      
      // Try multiple field name variations
      const postedAt = request.postedAt || request.posted_at || request.createdAt || request.created_at || request.createdDate || request.created_date
      const matchedAt = request.matchedAt || request.matched_at || request.matchedDate || request.matched_date
      
      if (isNewTab) {
        // Age from postedAt till now
        return calculateAge(postedAt)
      } else if (isPendingConfirmationTab) {
        // Age from postedAt till matchedAt
        return calculateAge(postedAt, matchedAt)
      } else if (isPendingMatchingTab) {
        // Age from matchedAt till now
        return calculateAge(matchedAt)
      }
      return '-'
    }
    
    if (options?.priceBreakdown) {
      const priceCell = renderPriceBreakdownCell(key, value, request?.currency)
      if (priceCell) return priceCell
    }

    const entityCell = renderEntityCell(key, value, entityLookups, setAccountDetailId)
    if (entityCell) return entityCell

    // Handle null, undefined, empty string, and "null" string
    if (value === null || value === undefined || value === '' || value === 'null') return '-'
    
    // Handle cargos count (for New tab)
    if (key === 'cargos') {
      if (Array.isArray(value)) {
        return value.length
      }
      return 0
    }
    
    // Handle shipments count (for other tabs)
    if (key === 'shipments') {
      if (Array.isArray(value)) {
        return value.length
      }
      return 0
    }

    // Handle duration fields stored as minutes — display as H:M
    if (isDurationMinutesField(key)) {
      const minutes = typeof value === 'number' ? value : Number(value)
      if (!Number.isNaN(minutes)) {
        return formatDurationMinutesAsHm(minutes)
      }
    }

    // Handle boolean
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    
    // Handle arrays (but not cargos)
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    
    // Handle date-time strings
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return new Date(value).toLocaleString()
    }
    
    // Handle enum fields - try to localize
    const enumName = getEnumNameFromKey(key)
    if (enumName) {
      const localized = getLocalizedLabel(enumName, String(value))
      if (localized !== String(value)) {
        // Return as Chip for enum values
        return (
          <Chip
            label={localized}
            size="small"
            color={
              String(value).toUpperCase() === 'URGENT' || String(value).toUpperCase() === 'EMERGENCY'
                ? 'error'
                : String(value).toUpperCase() === 'COMPLETED' || String(value).toUpperCase() === 'VERIFIED'
                ? 'success'
                : 'default'
            }
          />
        )
      }
    }
    
    // Handle status field specifically - try both shipmentRequestStatus and shipmentStatus
    if (key.toLowerCase() === 'status') {
      const statusValue = String(value)
      let localizedStatus = getLocalizedLabel('shipmentRequestStatus', statusValue)
      if (localizedStatus === statusValue) {
        localizedStatus = getLocalizedLabel('shipmentStatus', statusValue)
      }
      return (
        <Chip
          label={localizedStatus}
          size="small"
          color={
            statusValue.toUpperCase() === 'COMPLETED' || statusValue.toUpperCase() === 'DELIVERED'
              ? 'success'
              : statusValue.toUpperCase() === 'IN_PROGRESS' || statusValue.toUpperCase() === 'PLANNED' || statusValue.toUpperCase() === 'IN_TRANSIT'
              ? 'info'
              : statusValue.toUpperCase() === 'PARTIALLY_PLANNED'
              ? 'warning'
              : statusValue.toUpperCase() === 'CANCELLED'
              ? 'error'
              : 'default'
          }
        />
      )
    }
    
    // Handle urgency field specifically
    if (key.toLowerCase() === 'urgency') {
      const urgencyValue = String(value)
      const localizedUrgency = getLocalizedLabel('shipmentUrgency', urgencyValue)
      return (
        <Chip
          label={localizedUrgency}
          size="small"
          color={
            urgencyValue.toUpperCase() === 'URGENT' || urgencyValue.toUpperCase() === 'EMERGENCY'
              ? 'error'
              : 'default'
          }
        />
      )
    }
    
    return String(value)
  }

  const toggleRowExpansion = (requestId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(requestId)) {
        newSet.delete(requestId)
      } else {
        newSet.add(requestId)
      }
      return newSet
    })
  }

  const toggleShipmentExpansion = (shipmentId: string) => {
    setExpandedShipments((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(shipmentId)) {
        newSet.delete(shipmentId)
      } else {
        newSet.add(shipmentId)
      }
      return newSet
    })
  }

  const isRowExpanded = (requestId: string) => expandedRows.has(requestId)
  const isShipmentExpanded = (shipmentId: string) => expandedShipments.has(shipmentId)

  const handleExportJson = (request: Record<string, unknown>) => {
    const id = request.id
    if (typeof id !== 'string' || !id) return
    exportJson(`shipment-request-${id}.json`, request)
    toast.success('Shipment request exported')
  }

  return (
    <Box className="animate-fade-in" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Time Window
                </Typography>
                <ToggleButtonGroup
                  value={timeWindow}
                  exclusive
                  onChange={handleTimeWindowChange}
                  aria-label="time window filter"
                  size="small"
                >
                  <ToggleButton value="LAST_30_DAYS" aria-label="last 30 days">
                    Last 30 Days
                  </ToggleButton>
                  <ToggleButton value="TODAY" aria-label="today">
                    Today
                  </ToggleButton>
                  <ToggleButton value="NEXT_7_DAYS" aria-label="next 7 days">
                    Next 7 Days
                  </ToggleButton>
                  <ToggleButton value="NEXT_30_DAYS" aria-label="next 30 days">
                    Next 30 Days
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <TextField
                select
                label="Shipper"
                value={shipperFilter}
                onChange={(e) => setShipperFilter(e.target.value)}
                sx={{ minWidth: 220 }}
                size="small"
              >
                <MenuItem value="">All shippers</MenuItem>
                {shipperAccounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.businessName || `${a.id.substring(0, 8)}…`}
                  </MenuItem>
                ))}
              </TextField>
              {shipperFilter && (
                <Chip
                  label={`Shipper: ${accountDisplayName(shipperFilter, entityLookups.accountById)}`}
                  size="small"
                  onDelete={() => setShipperFilter('')}
                  color="primary"
                  variant="outlined"
                  sx={{ alignSelf: 'center', mt: 2.5 }}
                />
              )}
            </Box>
            <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
          </Stack>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {statusTabs.map((status) => {
            const count = getStatusCount(status)
            const label = getLocalizedLabel('shipmentRequestStatus', status) || status
            return (
              <Tab
                key={status}
                label={
                  <Box key={`tab-label-${status}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{label}</span>
                    <Chip
                      key={`tab-chip-${status}`}
                      label={count}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '0.75rem',
                        bgcolor: 'grey.300',
                        color: 'text.primary',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                }
              />
            )
          })}
        </Tabs>

        {/* Tab Panels */}
        {statusTabs.map((status, index) => (
          <TabPanel key={status} value={activeTab} index={index}>
            {isLoading ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <TableSkeleton rows={8} columns={8} />
              </Box>
            ) : isError ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ErrorCard
                  title="Failed to load shipment requests"
                  message={(error as Error)?.message}
                  onRetry={() => refetch()}
                />
              </Box>
            ) : !shipmentRequests || shipmentRequests.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmptyState
                  title="No shipment requests found"
                  description={`No ${getLocalizedLabel('shipmentRequestStatus', status) || status} requests in the selected time window.`}
                />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  maxHeight: 'calc(100vh - 300px)',
                  overflow: 'auto',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead
                    ref={rootHeadRef}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      backgroundColor: 'background.paper',
                      '& th': {
                        backgroundColor: 'background.paper',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                      },
                    }}
                  >
                    <TableRow>
                      <ResizableTableCell width={50}>{''}</ResizableTableCell>
                      {columns.map((column) => (
                        <ResizableTableCell key={column}>
                          {formatColumnLabel(column)}
                        </ResizableTableCell>
                      ))}
                      <ResizableTableCell width={110} sx={stickyActionsHeaderSx}>
                        Actions
                      </ResizableTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shipmentRequests.map((request: any, idx: number) => {
                      const requestId = request.id || `row-${idx}`
                      const requestStatus = getRequestStatus(request)
                      const resetToNewEnabled =
                        Boolean(request.id) && canResetShipmentRequestToNew(requestStatus)
                      const isExpanded = isRowExpanded(requestId)
                      const cargos = Array.isArray(request.cargos) ? request.cargos : []
                      const shipments = Array.isArray(request.shipments) ? request.shipments : []
                      const hasExpandableContent = isNewTab ? cargos.length > 0 : shipments.length > 0
                      
                      const cargoColumns = cargos.length > 0
                        ? Object.keys(cargos[0]).filter((key) => shouldIncludeTableColumn(key, cargos[0][key]))
                        : []
                      
                      const shipmentColumns = shipments.length > 0
                        ? Object.keys(shipments[0]).filter((key) =>
                            shouldIncludeTableColumn(key, shipments[0][key], ['cargos'])
                          )
                        : []

                      // Add Cargos column at the beginning of shipment columns
                      const shipmentColumnsWithCargos = ['cargos', ...shipmentColumns]

                      return (
                        <React.Fragment key={requestId}>
                          <TableRow key={requestId} hover>
                            <TableCell>
                              {hasExpandableContent && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRowExpansion(requestId)}
                                  sx={{ p: 0.5 }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                            </TableCell>
                            {columns.map((column) => (
                              <TableCell key={column}>
                                {formatValue(
                                  column,
                                  column === 'age' ? null : request[column],
                                  request,
                                  { priceBreakdown: true }
                                )}
                              </TableCell>
                            ))}
                            <TableCell sx={stickyActionsBodySx}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title="Export JSON (backend payload as-is)">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!request.id}
                                      onClick={() => handleExportJson(request)}
                                    >
                                      <FileDownloadOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    resetToNewEnabled
                                      ? 'Reset to NEW (picked up at next matching round)'
                                      : 'Only available for NEW, Pending Confirmation, or Pending Matching'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!resetToNewEnabled || resetToNewMutation.isPending}
                                      onClick={() =>
                                        request.id &&
                                        setResetConfirmTarget({ id: request.id, status: requestStatus })
                                      }
                                    >
                                      <RestartAltIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {isExpanded && isNewTab && cargos.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={columns.length + 2} sx={{ py: 0, border: 0, bgcolor: 'action.hover' }}>
                                <Box sx={{ pl: 4, pr: 2, py: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Cargos ({cargos.length})
                                  </Typography>
                                  <Table size="small" sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        {cargoColumns.map((col) => (
                                          <ResizableTableCell
                                            key={col}
                                            stickyTop={rootHeaderHeight}
                                            sx={{ bgcolor: 'grey.100' }}
                                          >
                                            {col
                                              .replace(/([A-Z])/g, ' $1')
                                              .replace(/^./, (str) => str.toUpperCase())
                                              .trim()}
                                          </ResizableTableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {cargos.map((cargo: any, cargoIdx: number) => (
                                        <TableRow 
                                          key={cargo.id || cargoIdx}
                                          sx={{ 
                                            bgcolor: cargoIdx % 2 === 0 ? 'background.paper' : 'grey.50',
                                            '&:hover': {
                                              bgcolor: 'action.hover',
                                            },
                                          }}
                                        >
                                          {cargoColumns.map((col) => (
                                            <TableCell 
                                              key={col}
                                              sx={{ 
                                                bgcolor: 'inherit',
                                              }}
                                            >
                                              {formatValue(col, cargo[col], cargo)}
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                          {isExpanded && !isNewTab && shipments.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={columns.length + 2} sx={{ py: 0, border: 0, bgcolor: 'action.hover' }}>
                                <Box sx={{ pl: 4, pr: 2, py: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Shipments ({shipments.length})
                                  </Typography>
                                  <Table size="small" sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <ResizableTableCell
                                          width={50}
                                          stickyTop={rootHeaderHeight}
                                          sx={{ bgcolor: 'grey.100' }}
                                        >
                                          {''}
                                        </ResizableTableCell>
                                        {shipmentColumnsWithCargos.map((col) => (
                                          <ResizableTableCell
                                            key={col}
                                            stickyTop={rootHeaderHeight}
                                            sx={{ bgcolor: 'grey.100' }}
                                          >
                                            {col === 'cargos'
                                              ? 'Cargos'
                                              : col
                                                  .replace(/([A-Z])/g, ' $1')
                                                  .replace(/^./, (str) => str.toUpperCase())
                                                  .trim()}
                                          </ResizableTableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {shipments.map((shipment: any, shipmentIdx: number) => {
                                        const shipmentId = shipment.id || `shipment-${shipmentIdx}`
                                        const shipmentIsExpanded = isShipmentExpanded(shipmentId)
                                        const shipmentCargos = Array.isArray(shipment.cargos) ? shipment.cargos : []
                                        const shipmentCargoColumns = shipmentCargos.length > 0
                                          ? Object.keys(shipmentCargos[0]).filter((key) =>
                                              shouldIncludeTableColumn(key, shipmentCargos[0][key])
                                            )
                                          : []

                                        return (
                                          <React.Fragment key={shipmentId}>
                                            <TableRow
                                              sx={{ 
                                                bgcolor: shipmentIdx % 2 === 0 ? 'background.paper' : 'grey.50',
                                                '&:hover': {
                                                  bgcolor: 'action.hover',
                                                },
                                              }}
                                            >
                                              <TableCell>
                                                {shipmentCargos.length > 0 && (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => toggleShipmentExpansion(shipmentId)}
                                                    sx={{ p: 0.5 }}
                                                  >
                                                    {shipmentIsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                  </IconButton>
                                                )}
                                              </TableCell>
                                              {shipmentColumnsWithCargos.map((col) => (
                                                <TableCell 
                                                  key={col}
                                                  sx={{ 
                                                    bgcolor: 'inherit',
                                                  }}
                                                >
                                                  {col === 'cargos' ? (
                                                    shipmentCargos.length
                                                  ) : (
                                                    formatValue(col, shipment[col], shipment)
                                                  )}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                            {shipmentIsExpanded && shipmentCargos.length > 0 && (
                                              <TableRow>
                                                <TableCell colSpan={shipmentColumnsWithCargos.length + 1} sx={{ py: 0, border: 0, bgcolor: 'grey.100' }}>
                                                  <Box sx={{ pl: 6, pr: 2, py: 2 }}>
                                                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                                                      Cargos ({shipmentCargos.length})
                                                    </Typography>
                                                    <Table size="small" sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                                                      <TableHead>
                                                        <TableRow sx={{ bgcolor: 'grey.200' }}>
                                                          {shipmentCargoColumns.map((col) => (
                                                            <ResizableTableCell
                                                              key={col}
                                                              stickyTop={rootHeaderHeight + nestedHeaderHeight}
                                                              sx={{ bgcolor: 'grey.200' }}
                                                            >
                                                              {col
                                                                .replace(/([A-Z])/g, ' $1')
                                                                .replace(/^./, (str) => str.toUpperCase())
                                                                .trim()}
                                                            </ResizableTableCell>
                                                          ))}
                                                        </TableRow>
                                                      </TableHead>
                                                      <TableBody>
                                                        {shipmentCargos.map((cargo: any, cargoIdx: number) => (
                                                          <TableRow 
                                                            key={cargo.id || cargoIdx}
                                                            sx={{ 
                                                              bgcolor: cargoIdx % 2 === 0 ? 'background.paper' : 'grey.50',
                                                              '&:hover': {
                                                                bgcolor: 'action.hover',
                                                              },
                                                            }}
                                                          >
                                                            {shipmentCargoColumns.map((col) => (
                                                              <TableCell 
                                                                key={col}
                                                                sx={{ 
                                                                  bgcolor: 'inherit',
                                                                }}
                                                              >
                                                                {formatValue(col, cargo[col], cargo)}
                                                              </TableCell>
                                                            ))}
                                                          </TableRow>
                                                        ))}
                                                      </TableBody>
                                                    </Table>
                                                  </Box>
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </React.Fragment>
                                        )
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        ))}
      </Paper>

      <PriceBreakdownModal
        open={priceModal.open}
        title={priceModal.title}
        breakdown={priceModal.breakdown}
        onClose={() => setPriceModal({ open: false, title: '', breakdown: null })}
      />

      <ConfirmDialog
        open={resetConfirmTarget != null}
        title="Reset shipment request to NEW?"
        description={
          resetConfirmTarget ? (
            <Stack spacing={1}>
              <Typography variant="body2">
                This will reset the shipment request status to{' '}
                <strong>{getLocalizedLabel('shipmentRequestStatus', 'NEW') || 'NEW'}</strong>.
                It will be picked up at the next matching round.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current status:{' '}
                {getLocalizedLabel('shipmentRequestStatus', resetConfirmTarget.status) ||
                  resetConfirmTarget.status}
              </Typography>
            </Stack>
          ) : null
        }
        confirmLabel="Reset to NEW"
        severity="warning"
        loading={resetToNewMutation.isPending}
        onClose={() => !resetToNewMutation.isPending && setResetConfirmTarget(null)}
        onConfirm={() => resetConfirmTarget && resetToNewMutation.mutate(resetConfirmTarget.id)}
      />

      <AccountDetailDialog
        accountId={accountDetailId}
        open={!!accountDetailId}
        onClose={() => setAccountDetailId(null)}
      />
    </Box>
  )
}

